/**
 * The member app's own marks, for the four cards Thomas asked to carry a
 * visual element (Sep 9 2026, night). Redrawn the same night after his
 * review: the first pass was the landscape ridges flattened to one colour at
 * 14%, and a purple wash at 12% to nothing, and he read both as missing.
 * Decoration only: none of these encodes a value, and every one sits behind
 * or beside the figure it belongs to.
 *
 *   AscentMark          the web app's Milestones "Ascent" in its PORTRAIT
 *                       composition (ascent-scene.tsx PortraitScene, the
 *                       vertical mountain a phone shows), as a soft
 *                       watermark: the sky, the sun's glow, three hazed
 *                       ridges, the carved massif with its lit and shadow
 *                       faces, the gold crests and the snowfields. The
 *                       geometry is the scene's OWN vertex list, not a
 *                       redraw, and the palette is the one the mobile app's
 *                       AscentWatermark settled by sampling pixels
 *                       (p3-mobile, Aug 30 2026): the web's ramp warmed
 *                       toward rose, because the scene's stops are tuned to
 *                       sit under a full peach sky and go brown at a whisper.
 *                       The sky is drawn deliberately, since in the full
 *                       view it is most of the picture and carries the
 *                       warmth. The mark dissolves toward the copy through a
 *                       luminance mask, so it ends inside the card.
 *   PulseMark           the coach's tile (coach.route.tsx PULSE_TILE): the
 *                       purple gradient with the white spark-and-waveform
 *                       glyph (nav-icons.tsx PulseNavIcon).
 *   PulseWatermark      the same spark, large, wearing the gradient itself,
 *                       in the card body.
 *   QuestionsWatermark  the app's own Questions bubble (nav-icons.tsx
 *                       QuestionsNavIcon), large, plum, fading toward the
 *                       copy.
 *   QuestionGlyph       a speech bubble in the Overview tiles' 1.8 stroke,
 *   PeopleGlyph         and two people in the same stroke, for the KPI
 *                       tile frames on Questions asked and Roster coverage.
 */

import { useId } from "react";

function useSvgId(): string {
  return useId().replace(/[^a-zA-Z0-9]/g, "");
}

/* ---- The Ascent, portrait ------------------------------------------------
   ascent-scene.tsx draws the portrait scene in a 620 x 900 box with the
   summit at 432,162. This box keeps every one of those units in place and
   adds room: 200 above, so the sky runs up behind the figures and the summit
   sits below them, and 400 to the left, so the sky runs out under the copy
   before the mask dissolves it. The ridges and the massif are extended flat
   into that margin so nothing ends in a vertical cut. */
const VB_X = -400;
const VB_Y = -200;
const VB_W = 1020;
const VB_H = 1100;
const L = VB_X;
const B = 900;

const RIDGE_FF = `M${L},342 L0,342 Q70,296 150,320 Q212,344 300,288 L340,308 Q412,342 500,284 Q562,250 620,300 L620,${B} L${L},${B} Z`;
const RIDGE_FAR = `M${L},394 L0,394 Q60,330 150,362 L202,300 Q272,352 350,294 L400,260 Q472,318 560,266 Q596,246 620,286 L620,${B} L${L},${B} Z`;
const RIDGE_MID = `M${L},498 L0,498 Q95,442 205,470 Q305,496 405,406 Q505,322 600,398 Q614,410 620,404 L620,${B} L${L},${B} Z`;
const MASSIF = `M${L},${B} L${L},700 L0,700 L72,674 L150,632 L216,576 L268,500 L306,414 L340,470 L372,360 L400,262 L420,188 L432,162 L452,214 L484,318 L514,430 L548,352 L576,452 L602,560 L620,616 L620,${B} Z`;

/* The carved faces, verbatim: the shadow side away from the sun, the lit
   approach, and the smaller facets. The scene keeps its lit face at 0.09;
   at a watermark's opacity that vanished, so it takes the mobile mark's
   value instead, and the shadow faces keep the scene's own. */
const SHADOW_FACES: readonly [string, number][] = [
  ["M432,162 L452,214 L484,318 L514,430 L548,352 L576,452 L602,560 L620,616 L620,900 L470,900 Z", 0.4],
  ["M216,576 L268,500 L306,414 L330,900 L200,900 Z", 0.2],
  ["M340,470 L372,360 L400,900 L346,900 Z", 0.16],
  ["M514,430 L548,352 L566,900 L512,900 Z", 0.14],
];
const LIT_FACES: readonly [string, number][] = [
  ["M432,162 L420,188 L400,262 L372,360 L360,900 L420,900 Z", 1],
  ["M72,674 L150,632 L180,900 L60,900 Z", 0.5],
];
/* Striations down the faces, then the gold on the crests the sun reaches,
   then the snowfields. All verbatim. */
const STRIATIONS: readonly [string, "light" | "dark", number, number][] = [
  ["M150,632 C240,560 320,470 392,360", "light", 0.3, 1.6],
  ["M268,500 C320,430 380,330 420,220", "light", 0.24, 1.4],
  ["M470,300 C500,380 520,500 540,620", "dark", 0.3, 1.6],
  ["M512,420 C534,510 552,620 566,740", "dark", 0.22, 1.4],
];
const CRESTS: readonly [string, number, number][] = [
  ["M400,262 L420,188 L432,162", 0.6, 2.8],
  ["M340,470 L372,360 L400,262", 0.36, 2],
  ["M306,414 L340,470", 0.24, 1.6],
];
const SNOW: readonly [string, number][] = [
  ["M420,190 L426,166 L432,162 L456,206 L444,196 L436,214 L426,198 L416,208 Z", 1],
  ["M388,290 L400,262 L414,286 L402,300 Z", 0.5],
  ["M494,340 L510,352 L500,372 L486,360 Z", 0.3],
];
/* The haze bands between the ridges: atmospheric perspective is what makes
   one ridge sit behind another rather than merely touch it. y, height,
   opacity, as the scene has them. */
const HAZE = [
  [296, 110, 1],
  [386, 120, 0.9],
  [470, 120, 0.8],
  [660, 150, 0.5],
] as const;

/* Every colour is a CSS custom property set on .e-card-mark-ascent, so the
   one SVG follows the theme: the day palette in light, twilight in dark. */
const v = (name: string) => ({ stopColor: `var(--asc-${name})` });

export function AscentMark({ className }: { className?: string }) {
  const id = useSvgId();
  const sky = `${id}sky`;
  const sun = `${id}sun`;
  const ff = `${id}ff`;
  const far = `${id}far`;
  const mid = `${id}mid`;
  const ms = `${id}ms`;
  const haze = `${id}hz`;
  const fadeRamp = `${id}fr`;
  const topRamp = `${id}tr`;
  const fade = `${id}fade`;
  const facesRamp = `${id}facr`;
  const facesMask = `${id}facm`;
  return (
    <svg
      className={className ? `e-card-mark e-card-mark-ascent ${className}` : "e-card-mark e-card-mark-ascent"}
      viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMaxYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={sky} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={v("sky-0")} stopOpacity="0.5" />
          <stop offset="0.45" style={v("sky-1")} stopOpacity="0.78" />
          <stop offset="1" style={v("sky-2")} stopOpacity="0.95" />
        </linearGradient>
        {/* The carved faces fade before the base (Thomas, Sep 18 2026: the
            vertical lines on the peak were glaring). Each face is a polygon
            the scene closes with a near-vertical edge running to the foot of
            the box; at card size those edges cut the massif into bands. The
            mask keeps the carving at the summit and dissolves it by the
            mid-slopes, so the faces read as light on rock, not as lines. */}
        <linearGradient id={facesRamp} gradientUnits="userSpaceOnUse" x1="0" y1="150" x2="0" y2="560">
          <stop offset="0" stopColor="#fff" stopOpacity="1" />
          <stop offset="0.45" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={facesMask} maskUnits="userSpaceOnUse" x={VB_X} y={VB_Y} width={VB_W} height={VB_H}>
          <rect x={VB_X} y={VB_Y} width={VB_W} height={VB_H} fill={`url(#${facesRamp})`} />
        </mask>
        <radialGradient id={sun} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" style={v("sun-0")} stopOpacity="1" />
          <stop offset="0.45" style={v("sun-1")} stopOpacity="0.7" />
          <stop offset="1" style={v("sun-1")} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={ff} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={v("ff-0")} />
          <stop offset="1" style={v("ff-1")} />
        </linearGradient>
        <linearGradient id={far} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={v("far-0")} />
          <stop offset="1" style={v("far-1")} />
        </linearGradient>
        <linearGradient id={mid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={v("mid-0")} />
          <stop offset="1" style={v("mid-1")} />
        </linearGradient>
        <linearGradient id={ms} x1="0.15" x2="0.85" y1="0" y2="1">
          <stop offset="0" style={v("ms-0")} />
          <stop offset="0.55" style={v("ms-1")} />
          <stop offset="1" style={v("ms-2")} />
        </linearGradient>
        <linearGradient id={haze} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={v("haze")} stopOpacity="0" />
          <stop offset="0.5" style={v("haze")} stopOpacity="0.85" />
          <stop offset="1" style={v("haze")} stopOpacity="0" />
        </linearGradient>
        {/* Dissolve to the left: in a luminance mask white keeps, black
            drops. The copy sits at the left of the body; the mark ends before
            it reaches it. */}
        <linearGradient id={fadeRamp} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#000" />
          <stop offset="0.3" stopColor="#2e2e2e" />
          <stop offset="0.68" stopColor="#c8c8c8" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
        {/* And upward, so the sky ends by fading rather than at the body's
            edge under the figures. */}
        <linearGradient id={topRamp} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#000" stopOpacity="1" />
          <stop offset="0.55" stopColor="#000" stopOpacity="0.45" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </linearGradient>
        <mask id={fade} maskUnits="userSpaceOnUse" x={VB_X} y={VB_Y} width={VB_W} height={VB_H}>
          <rect x={VB_X} y={VB_Y} width={VB_W} height={VB_H} fill={`url(#${fadeRamp})`} />
          <rect x={VB_X} y={VB_Y} width={VB_W} height="260" fill={`url(#${topRamp})`} />
        </mask>
      </defs>

      <g mask={`url(#${fade})`}>
        <rect x={VB_X} y={VB_Y} width={VB_W} height={VB_H} fill={`url(#${sky})`} />
        {/* The sun's glow behind the summit, where the scene puts it. A
            glow, not a disc: the disc read as a dot. */}
        <circle cx="500" cy="170" r="230" fill={`url(#${sun})`} />

        <path d={RIDGE_FF} fill={`url(#${ff})`} opacity="0.7" />
        <rect x={VB_X} y={HAZE[0][0]} width={VB_W} height={HAZE[0][1]} fill={`url(#${haze})`} opacity={HAZE[0][2]} />
        <path d={RIDGE_FAR} fill={`url(#${far})`} opacity="0.92" />
        <rect x={VB_X} y={HAZE[1][0]} width={VB_W} height={HAZE[1][1]} fill={`url(#${haze})`} opacity={HAZE[1][2]} />
        <path d={RIDGE_MID} fill={`url(#${mid})`} opacity="0.98" />
        <rect x={VB_X} y={HAZE[2][0]} width={VB_W} height={HAZE[2][1]} fill={`url(#${haze})`} opacity={HAZE[2][2]} />

        <path d={MASSIF} fill={`url(#${ms})`} />
        <g opacity="0.6" mask={`url(#${facesMask})`}>
          {SHADOW_FACES.map(([d, o]) => (
            <path key={d} d={d} className="e-asc-shadow" opacity={o} />
          ))}
          {LIT_FACES.map(([d, o]) => (
            <path key={d} d={d} className="e-asc-lit" opacity={o} />
          ))}
        </g>
        <g fill="none" strokeLinecap="round">
          {STRIATIONS.map(([d, tone, o, w]) => (
            <path
              key={d}
              d={d}
              className={tone === "light" ? "e-asc-striation-light" : "e-asc-striation-dark"}
              strokeOpacity={o}
              strokeWidth={w}
            />
          ))}
        </g>
        <rect x={VB_X} y={HAZE[3][0]} width={VB_W} height={HAZE[3][1]} fill={`url(#${haze})`} opacity={HAZE[3][2]} />
        <g fill="none" strokeLinecap="round" className="e-asc-crest">
          {CRESTS.map(([d, o, w]) => (
            <path key={d} d={d} opacity={o} strokeWidth={w} />
          ))}
        </g>
        <g className="e-asc-snow">
          {SNOW.map(([d, o]) => (
            <path key={d} d={d} opacity={o} />
          ))}
        </g>
      </g>
    </svg>
  );
}

/* ---- Pulse AI --------------------------------------------------------------
   nav-icons.tsx PulseNavIcon: a four-point spark with the waveform cut out
   of it. The same path at 24 in the header tile and at the body's height as
   the watermark. */
const SPARK = "M12 2.6Q13.4 10.6 21.4 12Q13.4 13.4 12 21.4Q10.6 13.4 2.6 12Q10.6 10.6 12 2.6Z";
const WAVEFORM = "M8.6 12h1l1.2-2.4 2.4 4.8 1.2-2.4h1";

function SparkMask({ id, stroke }: { id: string; stroke: number }) {
  return (
    <mask id={id}>
      <rect width="24" height="24" fill="#fff" />
      <path
        d={WAVEFORM}
        fill="none"
        stroke="#000"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </mask>
  );
}

/** The spark with the waveform cut out, as the web app draws it. */
export function PulseGlyph({ className }: { className?: string }) {
  const maskId = useSvgId();
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <SparkMask id={maskId} stroke={2} />
      </defs>
      <path d={SPARK} mask={`url(#${maskId})`} />
    </svg>
  );
}

export function PulseMark() {
  return (
    <span className="e-pulse-mark" aria-hidden="true">
      <PulseGlyph />
    </span>
  );
}

/** The coach's spark, large, in its own gradient, at the foot of the body. */
export function PulseWatermark() {
  const id = useSvgId();
  return (
    <svg
      className="e-card-mark e-card-mark-pulse"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMaxYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}g`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: "var(--pulse-g0)" }} />
          <stop offset="1" style={{ stopColor: "var(--pulse-g1)" }} />
        </linearGradient>
        <SparkMask id={`${id}m`} stroke={1.6} />
      </defs>
      <path d={SPARK} fill={`url(#${id}g)`} mask={`url(#${id}m)`} />
    </svg>
  );
}

/* ---- Questions asked -------------------------------------------------------
   nav-icons.tsx QuestionsNavIcon: the app's own bubble with three dots. */
const BUBBLE =
  "M12 3.3c-5.08 0-9 3.36-9 7.55 0 2.32 1.26 4.42 3.3 5.83-.18 1.02-.66 2.07-1.5 3-.34.38-.02.99.48.9 1.94-.34 3.5-1 4.66-1.74.67.1 1.36.16 2.06.16 5.08 0 9-3.36 9-7.55S17.08 3.3 12 3.3ZM8.4 9.65a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Zm3.6 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Zm3.6 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z";

/** The bubble, large and plum, fading toward the copy at its top left. */
export function QuestionsWatermark() {
  const id = useSvgId();
  return (
    <svg
      className="e-card-mark e-card-mark-questions"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMaxYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}g`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
      </defs>
      <path d={BUBBLE} fill={`url(#${id}g)`} fillRule="evenodd" clipRule="evenodd" />
    </svg>
  );
}

/* ---- The tile glyphs, in the Overview tiles' frame: 24-box, 1.8 stroke. */
function TileGlyph({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function QuestionGlyph() {
  return (
    <TileGlyph>
      <path d="M21 12a8 8 0 0 1-11.4 7.2L4 21l1.8-5.6A8 8 0 1 1 21 12z" />
      <path d="M9.6 9.6a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1 .9-1 1.6" />
      <path d="M12 16.4h.01" />
    </TileGlyph>
  );
}

export function PeopleGlyph() {
  return (
    <TileGlyph>
      <circle cx="9" cy="7.2" r="3.4" />
      <path d="M15.2 20v-1.8a4.2 4.2 0 0 0-4.2-4.2H7a4.2 4.2 0 0 0-4.2 4.2V20" />
      <path d="M16 4.1a3.4 3.4 0 0 1 0 6.2" />
      <path d="M21.2 20v-1.8a4.2 4.2 0 0 0-3-4" />
    </TileGlyph>
  );
}
